import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, ClipboardCheck, Dices, Gift, PackageSearch, Save, Scale, ServerCog, ShieldCheck, Sparkles, Star } from 'lucide-react';

import { API_BASE_URL } from '../../config';
import ManualBonusAdjustmentPanel from './ManualBonusAdjustmentPanel';
import RouletteFulfillmentPanel from './RouletteFulfillmentPanel';
import RouletteSettingsPanel from './RouletteSettingsPanel';
import GiftDeliverySettingsPanel from './GiftDeliverySettingsPanel';
import GiftCatalogPanel from './GiftCatalogPanel';

const DEFAULTS = {
    enabled: false,
    bonus_per_star: 1,
    premium_3_months: 0,
    premium_6_months: 0,
    premium_12_months: 0,
    claim_ttl_days: 30,
};

const numberValue = (value, fallback = 0) => {
    const parsed = Number.parseInt(String(value), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const NumberField = ({ id, label, value, onChange, min = 0, max, hint }) => (
    <label htmlFor={id} className="block">
        <span className="mb-2 block text-sm font-medium text-slate-700">{label}</span>
        <input
            id={id}
            name={id}
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            step="1"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-base font-semibold text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
        {hint && <span className="mt-2 block text-xs leading-5 text-slate-500">{hint}</span>}
    </label>
);

const LoyaltySettingsPage = ({ onShowToast }) => {
    const [activeTab, setActiveTab] = useState('bonuses');
    const [settings, setSettings] = useState(DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await fetch(`${API_BASE_URL}/api/settings/loyalty`, {
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (!response.ok) throw new Error('Не удалось загрузить настройки');
                setSettings({ ...DEFAULTS, ...(await response.json()) });
            } catch (loadError) {
                setError(loadError.message || 'Не удалось загрузить настройки');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const preview = useMemo(() => {
        const rate = Math.max(0, numberValue(settings.bonus_per_star, 1));
        return 100 * rate;
    }, [settings.bonus_per_star]);

    const update = (key, value) => setSettings((current) => ({ ...current, [key]: value }));

    const handleSave = async () => {
        const payload = {
            enabled: Boolean(settings.enabled),
            bonus_per_star: Math.max(0, numberValue(settings.bonus_per_star, 1)),
            premium_3_months: Math.max(0, numberValue(settings.premium_3_months)),
            premium_6_months: Math.max(0, numberValue(settings.premium_6_months)),
            premium_12_months: Math.max(0, numberValue(settings.premium_12_months)),
            claim_ttl_days: Math.max(1, numberValue(settings.claim_ttl_days, 30)),
        };

        try {
            setSaving(true);
            setError('');
            const response = await fetch(`${API_BASE_URL}/api/settings/loyalty`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось сохранить настройки');
            setSettings({ ...DEFAULTS, ...body });
            if (onShowToast) onShowToast('Настройки бонусной программы сохранены');
        } catch (saveError) {
            setError(saveError.message || 'Не удалось сохранить настройки');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="py-16 text-center text-slate-500">Загрузка бонусной программы...</div>;
    }

    return (
        <div className="mx-auto max-w-7xl space-y-6 pb-12">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium text-indigo-600">
                        <Gift size={17} /> Основной сервис
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Бонусная программа</h2>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                        Начисления, правила рулетки и выдача выигрышей основного сервиса. Платёжные формулы здесь не меняются.
                    </p>
                </div>
                {activeTab === 'bonuses' && (
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Save size={18} /> {saving ? 'Сохранение...' : 'Сохранить начисления'}
                    </button>
                )}
            </header>

            <nav className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-slate-100 p-1" aria-label="Разделы бонусной программы">
                {[
                    { id: 'bonuses', label: 'Начисления', icon: Sparkles },
                    { id: 'adjustments', label: 'Корректировки', icon: Scale },
                    { id: 'roulette', label: 'Рулетка', icon: Dices },
                    { id: 'gift-catalog', label: 'Каталог подарков', icon: PackageSearch },
                    { id: 'gift-delivery', label: 'Автовыдача', icon: ServerCog },
                    { id: 'fulfillment', label: 'Выдача призов', icon: ClipboardCheck },
                ].map((tab) => {
                    const Icon = tab.icon;
                    const selected = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={`inline-flex h-10 min-w-max flex-1 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition ${selected ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                        >
                            <Icon size={17} /> {tab.label}
                        </button>
                    );
                })}
            </nav>

            {activeTab === 'bonuses' && error && (
                <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {error}
                </div>
            )}

            {activeTab === 'bonuses' && (
                <>
                    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                                    <Sparkles size={20} />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-900">Начислять бонусы после успешной оплаты</h3>
                                    <p className="mt-1 text-sm leading-5 text-slate-500">
                                        Работает только в основном боте, основном Mini App и на сайте.
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={settings.enabled}
                                onClick={() => update('enabled', !settings.enabled)}
                                className={`relative h-7 w-12 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 ${settings.enabled ? 'bg-indigo-600' : 'bg-slate-300'}`}
                            >
                                <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${settings.enabled ? 'left-6' : 'left-1'}`} />
                                <span className="sr-only">Включить бонусную программу</span>
                            </button>
                        </div>

                        <div className={`mt-5 rounded-lg border px-4 py-3 text-sm ${settings.enabled ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                            {settings.enabled
                                ? 'Программа включена. Новые подходящие покупки будут начислять бонусы.'
                                : 'Программа выключена. Настройки можно подготовить и включить после проверки экономики.'}
                        </div>
                    </section>

                    <section className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)]">
                        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                            <div className="mb-5 flex items-center gap-3">
                                <Star size={20} className="text-amber-500" />
                                <div>
                                    <h3 className="font-semibold text-slate-900">Начисления за покупки</h3>
                                    <p className="mt-1 text-sm text-slate-500">Целые бонусы, без дробных значений.</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <NumberField
                                    id="bonus_per_star"
                                    label="Бонусов за 1 купленную Star"
                                    value={settings.bonus_per_star}
                                    onChange={(value) => update('bonus_per_star', value)}
                                    max={1000}
                                    hint={`Пример: за 100 Stars пользователь получит ${preview.toLocaleString('ru-RU')} бонусов.`}
                                />

                                <div className="border-t border-slate-100 pt-5">
                                    <div className="mb-4">
                                        <div className="font-medium text-slate-800">Telegram Premium</div>
                                        <div className="mt-1 text-sm text-slate-500">Фиксированное начисление для каждого тарифа.</div>
                                    </div>
                                    <div className="grid gap-4 sm:grid-cols-3">
                                        <NumberField
                                            id="premium_3_months"
                                            label="3 месяца"
                                            value={settings.premium_3_months}
                                            onChange={(value) => update('premium_3_months', value)}
                                            max={1000000}
                                        />
                                        <NumberField
                                            id="premium_6_months"
                                            label="6 месяцев"
                                            value={settings.premium_6_months}
                                            onChange={(value) => update('premium_6_months', value)}
                                            max={1000000}
                                        />
                                        <NumberField
                                            id="premium_12_months"
                                            label="12 месяцев"
                                            value={settings.premium_12_months}
                                            onChange={(value) => update('premium_12_months', value)}
                                            max={1000000}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                                <div className="mb-4 flex items-center gap-3">
                                    <CalendarClock size={20} className="text-indigo-600" />
                                    <h3 className="font-semibold text-slate-900">Гостевая покупка</h3>
                                </div>
                                <NumberField
                                    id="claim_ttl_days"
                                    label="Срок получения бонусов, дней"
                                    value={settings.claim_ttl_days}
                                    onChange={(value) => update('claim_ttl_days', value)}
                                    min={1}
                                    max={365}
                                    hint="После этого срока незарегистрированный покупатель не сможет забрать начисление."
                                />
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
                                <div className="flex gap-3">
                                    <ShieldCheck size={20} className="mt-0.5 shrink-0 text-slate-600" />
                                    <div>
                                        <h3 className="font-semibold text-slate-900">Границы программы</h3>
                                        <ul className="mt-3 space-y-2 text-sm leading-5 text-slate-600">
                                            <li>Партнёрские боты не участвуют.</li>
                                            <li>Прочие цифровые товары не участвуют.</li>
                                            <li>Повторный webhook не начислит бонусы дважды.</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </>
            )}

            {activeTab === 'adjustments' && <ManualBonusAdjustmentPanel onShowToast={onShowToast} />}
            {activeTab === 'roulette' && <RouletteSettingsPanel onShowToast={onShowToast} />}
            {activeTab === 'gift-catalog' && <GiftCatalogPanel onShowToast={onShowToast} />}
            {activeTab === 'gift-delivery' && <GiftDeliverySettingsPanel onShowToast={onShowToast} />}
            {activeTab === 'fulfillment' && <RouletteFulfillmentPanel onShowToast={onShowToast} />}
        </div>
    );
};

export default LoyaltySettingsPage;
